import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/modern/components/ui/dialog-shadcn';
import { Button } from '@/modern/components/ui/button';
import { Checkbox } from '@/modern/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/modern/components/ui/radio-group';
import { Label } from '@/modern/components/ui/label';
import { ScrollArea } from '@/modern/components/ui/scroll-area';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { bulkCopyEntitiesToBoard, copyEntityToOtherBoard } from '@/network/network';
import { cn } from '@/modern/utils';
import { toast } from 'sonner';
import BryntumContext from '@/react/context/bryntumContext';
import { DuplicateEntityResolutionDialog } from './DuplicateEntityResolutionDialog';
import type { CopyTaskResponse } from '@/types/CopyTask';
import { capitalizeText } from '@/other/utils/utils';
import { EDITOR_KEYS } from '@/other/constants/EditorKeys';
import { shouldShowAsyncDialog } from '@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup';

// ─── Sub-components ──────────────────────────────────────────────────────────

interface SelectableCardProps {
  value: string;
  selectedValue: string;
  onSelect: (value: string) => void;
  children: React.ReactNode;
}

const SelectableCard: React.FC<SelectableCardProps> = ({ value, selectedValue, onSelect, children }) => (
  <div
    className={cn(
      'rounded-lg border p-4 cursor-pointer transition-all',
      selectedValue === value
        ? 'border-primary bg-primary/10 shadow-sm'
        : 'border-input hover:border-primary/50 hover:bg-accent/50'
    )}
    onClick={() => onSelect(value)}
  >
    <div className="flex items-start space-x-3">
      <RadioGroupItem value={value} id={`selectable-${value}`} className="mt-0.5 pointer-events-none" />
      <div className="flex-1">{children}</div>
    </div>
  </div>
);

interface CopyOptionsProps {
  copyMetadata: boolean;
  archiveOriginal: boolean;
  archiveLabel: string;
  onCopyMetadataChange: (v: boolean) => void;
  onArchiveChange: (v: boolean) => void;
}

const CopyOptions: React.FC<CopyOptionsProps> = ({
  copyMetadata,
  archiveOriginal,
  archiveLabel,
  onCopyMetadataChange,
  onArchiveChange,
}) => (
  <div className="space-y-3 pt-4">
    <div className="flex items-start space-x-3">
      <Checkbox
        id="copy-metadata"
        checked={copyMetadata}
        onCheckedChange={(checked) => onCopyMetadataChange(checked as boolean)}
        className="mt-0.5"
      />
      <div className="flex-1">
        <Label htmlFor="copy-metadata" className="text-sm font-medium leading-none cursor-pointer">
          Copy category, tags, status, team and phase selections
        </Label>
        <p className="text-xs text-muted-foreground mt-1">
          This option may alter the organization of the target board
        </p>
      </div>
    </div>

    <div className="flex items-start space-x-3">
      <Checkbox
        id="archive-original"
        checked={archiveOriginal}
        onCheckedChange={(checked) => onArchiveChange(checked as boolean)}
        className="mt-0.5"
      />
      <div className="flex-1">
        <Label htmlFor="archive-original" className="text-sm font-medium leading-none cursor-pointer">
          {archiveLabel}
        </Label>
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface CopyToBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records?: any[];
  entityType: string;
  entityName?: string;
}

export const CopyToBoardDialog: React.FC<CopyToBoardDialogProps> = ({
  open,
  onOpenChange,
  record,
  records,
  entityType,
  entityName,
}) => {
  const { boardManager, selectedBoard, editorHandler } = useContext(BryntumContext);
  const [selectedBoardId, setSelectedBoardId] = useState<number | string | null>(null);
  const [copyMetadata, setCopyMetadata] = useState(false);
  const [archiveOriginal, setArchiveOriginal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Bulk mode: two-step flow
  const [step, setStep] = useState<1 | 2>(1);
  const [bulkDuplicateAction, setBulkDuplicateAction] = useState<'duplicate' | 'replace'>('duplicate');

  // Single mode: post-response duplicate resolution
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedBoardName, setSelectedBoardName] = useState('');

  const isBulkMode = Array.isArray(records) && records.length > 0;
  const displayName = entityName || record?.name || 'this entity';
  const entityLabel = capitalizeText(entityType);

  useEffect(() => {
    if (open) {
      setSelectedBoardId(null);
      setCopyMetadata(false);
      setArchiveOriginal(false);
      setShowDuplicateModal(false);
      setSelectedBoardName('');
      setStep(1);
      setBulkDuplicateAction('duplicate');
    }
  }, [open]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const availableBoards: any[] = useMemo(() => {
    //@ts-expect-error boardManager is not typed in BryntumContext.
    if (!boardManager?.boardNameStore?.records) return [];
    //@ts-expect-error boardManager is not typed in BryntumContext.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return boardManager.boardNameStore.records.filter((board: any) =>
      board.id !== selectedBoard?.id && board.grants?.includes(GRANTS.grant_four)
    );
  }, [boardManager, selectedBoard]);

  const getBoardName = (boardId: number | string | null) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return availableBoards.find((b: any) => b.id === boardId)?.name || 'selected board';
  };

  const handleNext = () => {
    if (!selectedBoardId) return;
    setSelectedBoardName(getBoardName(selectedBoardId));
    setStep(2);
  };

  const handleCopy = async () => {
    if (!selectedBoardId) return;
    setIsLoading(true);
    try {
      if (isBulkMode) {
        const entityIds = records!.map((r) => r.id);
        await bulkCopyEntitiesToBoard(entityType, entityIds, selectedBoardId, {
          action: bulkDuplicateAction,
          copy_metadata: copyMetadata,
          archive_original: archiveOriginal,
        });
        const showDialog = shouldShowAsyncDialog('BULK_ENTITY_COPY');
        if (showDialog && editorHandler) {
          editorHandler.openEditor(
            EDITOR_KEYS.asyncTaskMessageEditor,
            null,
            { record: 'BULK_ENTITY_COPY' },
            { useExisting: true, by: 'recordType' }
          );
        }
        onOpenChange(false);
      } else {
        const response = await copyEntityToOtherBoard(entityType, record.id, selectedBoardId, {
          action: 'create_new',
          copy_metadata: copyMetadata,
          [`archive_original_${entityType}`]: archiveOriginal,
        });
        const data = response.data as CopyTaskResponse;
        if (data.duplicate_exists) {
          setSelectedBoardName(getBoardName(selectedBoardId));
          setShowDuplicateModal(true);
        } else {
          if (archiveOriginal && record) record.archived = true;
          toast.success(`${entityLabel} copied successfully`);
          onOpenChange(false);
        }
      }
    } catch {
      toast.error('Sorry, something went wrong. Unable to copy!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateResolve = async (action: 'replace' | 'duplicate', resolvedEntityName?: string) => {
    if (!selectedBoardId) return;
    try {
      await copyEntityToOtherBoard(entityType, record.id, selectedBoardId, {
        action,
        [`${entityType}_name`]: resolvedEntityName,
        copy_metadata: copyMetadata,
        [`archive_original_${entityType}`]: archiveOriginal,
      });
      if (archiveOriginal && record) record.archived = true;
      toast.success(`${entityLabel} copied successfully`);
      setShowDuplicateModal(false);
      onOpenChange(false);
    } catch (error) {
      toast.error('Sorry, something went wrong. Unable to copy!');
      throw error;
    }
  };

  const copyButton = (label: string) => (
    <Button onClick={handleCopy} disabled={isLoading} className="min-w-[160px]">
      {isLoading ? (
        <><i className="fa-light fa-spinner fa-spin mr-2" />Copying...</>
      ) : (
        <><i className="fa-light fa-copy mr-2" />{label}</>
      )}
    </Button>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal>
        <DialogContent className="sm:max-w-[600px]" onInteractOutside={(e) => e.preventDefault()}>

          {/* ── Step 1: Board selection ── */}
          {step === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {isBulkMode ? `Copy ${records!.length} ${entityLabel}s to board` : 'Copy to board'}
                </DialogTitle>
                <DialogDescription>
                  {isBulkMode ? (
                    <>Select the destination board for <span className="font-semibold">{records!.length} {entityType}s</span>.</>
                  ) : (
                    <>Select the board you want to copy <span className="font-semibold">"{displayName}"</span> to.</>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {availableBoards.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <i className="fa-light fa-inbox fa-3x mb-4 block opacity-20" />
                    <p className="text-base font-medium">No boards available</p>
                    <p className="text-sm mt-1">You need edit access to other boards.</p>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-[350px] pr-3">
                      <RadioGroup
                        value={selectedBoardId?.toString()}
                        onValueChange={setSelectedBoardId}
                        className="space-y-2"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {availableBoards.map((board: any) => (
                          <div
                            key={board.id}
                            className={cn(
                              'flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all',
                              selectedBoardId === board.id
                                ? 'border-primary bg-primary/10 shadow-sm'
                                : 'border-input hover:border-primary/50 hover:bg-accent/50'
                            )}
                            onClick={() => setSelectedBoardId(board.id)}
                          >
                            <RadioGroupItem
                              value={board.id.toString()}
                              id={`board-${board.id}`}
                              className="pointer-events-none"
                            />
                            <Label htmlFor={`board-${board.id}`} className="flex-1 text-sm font-medium leading-snug cursor-pointer">
                              {board.name}
                            </Label>
                            {selectedBoardId === board.id && (
                              <i className="fa-solid fa-check text-primary" />
                            )}
                          </div>
                        ))}
                      </RadioGroup>
                    </ScrollArea>

                    <CopyOptions
                      copyMetadata={copyMetadata}
                      archiveOriginal={archiveOriginal}
                      archiveLabel={isBulkMode
                        ? `After copying, archive these ${entityType}s on the source board`
                        : `After copying, archive this ${entityType} on the source board`}
                      onCopyMetadataChange={setCopyMetadata}
                      onArchiveChange={setArchiveOriginal}
                    />
                  </>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  onClick={isBulkMode ? handleNext : handleCopy}
                  disabled={!selectedBoardId || isLoading}
                  className="min-w-[140px]"
                >
                  {isLoading ? (
                    <><i className="fa-light fa-spinner fa-spin mr-2" />Copying...</>
                  ) : isBulkMode ? (
                    <>Next <i className="fa-light fa-arrow-right ml-2" /></>
                  ) : (
                    <><i className="fa-light fa-copy mr-2" />Copy to board</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {/* ── Step 2 (bulk only): Upfront duplicate handling ── */}
          {step === 2 && isBulkMode && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">Handle Duplicate Records</DialogTitle>
                <DialogDescription>
                  Some of the <span className="font-semibold">{records!.length} {entityType}s</span> you are copying may already exist on <span className="font-semibold">{selectedBoardName}</span>. Choose what should happen if a matching record is found.
                </DialogDescription>
              </DialogHeader>

              <div className="py-2">
                <RadioGroup value={bulkDuplicateAction} className="space-y-3">
                  <SelectableCard value="duplicate" selectedValue={bulkDuplicateAction} onSelect={(v) => setBulkDuplicateAction(v as 'duplicate' | 'replace')}>
                    <Label htmlFor="selectable-create_new" className="text-base font-semibold cursor-pointer">Duplicate</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create a copy alongside the existing record on <strong>{selectedBoardName}</strong>.
                    </p>
                  </SelectableCard>

                  <SelectableCard value="replace" selectedValue={bulkDuplicateAction} onSelect={(v) => setBulkDuplicateAction(v as 'duplicate' | 'replace')}>
                    <Label htmlFor="selectable-replace" className="text-base font-semibold cursor-pointer">Replace</Label>
                    <div className="flex items-center gap-1.5 text-sm text-destructive font-medium mt-1">
                      <i className="fa-solid fa-triangle-exclamation" />
                      This cannot be undone
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Overwrite any existing matching record on <strong>{selectedBoardName}</strong>.
                    </p>
                  </SelectableCard>
                </RadioGroup>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setStep(1)} disabled={isLoading}>
                  <i className="fa-light fa-arrow-left mr-2" />Back
                </Button>
                {copyButton(`Copy ${records!.length} to board`)}
              </DialogFooter>
            </>
          )}

        </DialogContent>
      </Dialog>

      {/* Duplicate resolution modal — single mode only, shown after API response */}
      {!isBulkMode && (
        <DuplicateEntityResolutionDialog
          open={showDuplicateModal}
          onOpenChange={setShowDuplicateModal}
          boardName={selectedBoardName}
          originalEntityName={displayName}
          entityType={entityType}
          onResolve={handleDuplicateResolve}
          onCancel={() => { setShowDuplicateModal(false); setSelectedBoardName(''); }}
          copyMetadata={copyMetadata}
          archiveOriginal={archiveOriginal}
        />
      )}
    </>
  );
};
